<template>
  <div class="satisfaction-survey">
    <Transition mode="out-in" name="step">
      <div :key="stepper.index.value" class="satisfaction-survey__content">
        <div class="satisfaction-survey__step-view">
          <SatisfactionStepHello v-if="stepper.isCurrent('hello-screen')" />
          <SatisfactionQuestion1
            v-if="stepper.isCurrent('question-1')"
            v-model:rating="formState['question-1'].rating"
            v-model:message="formState['question-1'].message"
          />
          <SatisfactionQuestion2
            v-if="stepper.isCurrent('question-2')"
            v-model:rating="formState['question-2'].rating"
            v-model:message="formState['question-2'].message"
          />
          <SatisfactionQuestion3
            v-if="stepper.isCurrent('question-3')"
            v-model:rating="formState['question-3'].rating"
            v-model:message="formState['question-3'].message"
          />
          <SatisfactionQuestion4
            v-if="stepper.isCurrent('question-4')"
            v-model:rating="formState['question-4'].rating"
            v-model:message="formState['question-4'].message"
          />
          <SatisfactionQuestion5
            v-if="stepper.isCurrent('question-5')"
            v-model:message="formState['question-5'].message"
          />
          <SatisfactionStepThankYou
            v-if="stepper.isCurrent('thank-you-screen')"
          />
        </div>
        <StepperFormSatisfactionSurveyControls
          :is-disabled="!stepper.current.value.isValid()"
          :current-step="stepper.index.value"
          :is-first-step="stepper.isFirst.value"
          :is-last-step="stepper.isLast.value"
          :is-loading="isLoading"
          @set-next-step="stepper.goToNext"
          @set-previous-step="stepper.goToPrevious"
          @submit-form="onSubmit"
        />
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { useStepper, useLocalStorage } from '@vueuse/core'

const { sendFormData } = useSendData()

const { query } = useRoute()
const email = query.email as string

const isLoading = ref(false)

const formState = useLocalStorage(`satisfaction-survey-form-${email}`, {
  'question-1': {
    rating: null,
    message: '',
  },
  'question-2': {
    rating: null,
    message: '',
  },
  'question-3': {
    rating: null,
    message: '',
  },
  'question-4': {
    rating: null,
    message: '',
  },
  'question-5': {
    message: '',
  },
})

const stepper = useStepper({
  'hello-screen': {
    isValid: () => true,
  },
  'question-1': {
    isValid: () => formState.value['question-1'].rating !== null,
  },
  'question-2': {
    isValid: () => formState.value['question-2'].rating !== null,
  },
  'question-3': {
    isValid: () => formState.value['question-3'].rating !== null,
  },
  'question-4': {
    isValid: () => formState.value['question-4'].rating !== null,
  },
  'question-5': {
    isValid: () => true,
  },
  'thank-you-screen': {
    isValid: () => true,
  },
})

const lastVisitedStep = useLocalStorage<Parameters<typeof stepper.goTo>[0]>(
  `satisfaction-survey-last-visited-step-${email}`,
  'hello-screen',
)

watch(stepper.index, () => {
  lastVisitedStep.value = stepper.stepNames.value[stepper.index.value]
})

if (lastVisitedStep.value !== 'hello-screen') {
  stepper.goTo(lastVisitedStep.value)
}

const onSubmit = async () => {
  const data = {
    submittedAt: Date.now(),
    fields: [
      {
        name: 'email',
        value: email,
      },
      {
        name: 'satisfaction_with_the_purchasing_process_rate',
        value: formState.value['question-1'].rating,
      },
      {
        name: 'satisfaction_with_the_purchasing_process_details',
        value: formState.value['question-1'].message,
      },
      {
        name: 'satisfaction_with_the_product_implementation_rate',
        value: formState.value['question-2'].rating,
      },
      {
        name: 'satisfaction_with_the_product_implementation_details',
        value: formState.value['question-2'].message,
      },
      {
        name: 'satisfaction_with_the_timelines_rate',
        value: formState.value['question-3'].rating,
      },
      {
        name: 'satisfaction_with_the_timeline_details',
        value: formState.value['question-3'].message,
      },
      {
        name: 'satisfaction_with_the_specific_attributes_rate',
        value: formState.value['question-4'].rating,
      },
      {
        name: 'satisfaction_with_the_specific_attributes_details',
        value: formState.value['question-4'].message,
      },
      {
        name: 'open_ended_feedback',
        value: formState.value['question-5'].message,
      },
    ],
    context: {
      pageUri: window.location.href,
      pageName: '/implementation-satisfaction-survey',
    },
  }

  isLoading.value = true

  await sendFormData(
    'https://api.hsforms.com/submissions/v3/integration/submit/5731513/4fadcad3-5e94-4345-87fa-608b86b7e547',
    data,
    {
      thankYouRoute: '',
    },
  )

  isLoading.value = false

  stepper.goTo('thank-you-screen')
}
</script>

<style lang="scss" scoped>
.step-enter-active,
.step-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}

.step-enter-from,
.step-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.satisfaction-survey {
  &__progress {
    height: 8px;
    border-radius: 4px;
    background-color: white;
    margin-bottom: 48px;
  }

  &__progress-bar {
    height: 100%;
    border-radius: 4px;
    background-color: var(--cobalt-400);
    transition: width 0.3s;
    margin-bottom: 48px;
  }

  &__step-view {
    position: relative;
    margin-bottom: 48px;
  }
}
</style>
